import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginDto } from '@/types'
import { apiService } from '@/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => {
    const result = !!token.value && !!user.value
    // console.log('isAuthenticated computed - token:', !!token.value, 'user:', !!user.value, 'result:', result)
    return result
  })

  const initializeAuth = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    // console.log('Initializing auth - storedToken:', storedToken, 'storedUser:', storedUser)

    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = JSON.parse(storedUser)
      // console.log('Auth initialized - user:', user.value, 'token:', token.value)
    } else {
      console.log('No stored auth data found')
    }
  }

  const login = async (credentials: LoginDto) => {
    try {
      loading.value = true
      console.log('Attempting login with:', credentials)
      const response = await apiService.login(credentials)
      console.log('Login response:', response)

      if (response.success) {
        console.log('Response data structure:', response.data)
        
        // Backend returns access_token
        const tokenValue = (response.data as any).access_token
        const userValue = response.data.user
        
        user.value = userValue
        token.value = tokenValue

        if (tokenValue) {
          localStorage.setItem('token', tokenValue)
          console.log('Token saved to localStorage:', tokenValue)
        } else {
          console.error('No token found in response!')
        }
        localStorage.setItem('user', JSON.stringify(userValue))

        console.log('Login successful, user:', user.value, 'token:', token.value)
        return { success: true }
      } else {
        console.log('Login failed:', response.message)
        return { success: false, error: response.message || 'Login failed' }
      }
    } catch (error: any) {
      console.error('Login error:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      user.value = null
      token.value = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  }

  const updateProfile = (updatedUser: User) => {
    user.value = updatedUser
    localStorage.setItem('user', JSON.stringify(updatedUser))
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    initializeAuth,
    login,
    logout,
    updateProfile,
  }
})
